import { Injectable, inject } from '@angular/core';
import {
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { AuthService } from '../../../features/auth/services/auth.service';
import apiRoute from '../../../common/constants/apiRoute';
import { from, switchMap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../../environments/environment';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private auth = inject(AuthService);
  private msalService = inject(MsalService);

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (req.method == 'JSONP') return next.handle(req);

    const handleRequest = (token: string | null) => {
      const headers: Record<string, string> = { Authorization: 'Bearer ' + token };

      // Local development impersonation; the API only honours this in Development
      if (environment.devAuth?.enabled) {
        headers['X-Dev-User'] =
          localStorage.getItem('devUser') || environment.devAuth.defaultUser;

        // Simulates the B2C "emails" claim, so invited-admin linking can be
        // exercised locally: localStorage.setItem('devEmail', 'x@y.do')
        const devEmail = localStorage.getItem('devEmail');
        if (devEmail) {
          headers['X-Dev-Email'] = devEmail;
        }
      }

      const authReq = req.clone({
        setHeaders: headers,
        url: apiRoute + req.url,
      });
      return next.handle(authReq);
    };

    if (environment.devAuth?.enabled) {
      return handleRequest('dev');
    }

    // Check if user is logged in via MSAL accounts
    const accounts = this.msalService.instance.getAllAccounts();
    const isLoggedIn = accounts.length > 0;

    // The profile request is what resolves profileReady, so it must never wait on it
    const isProfileRequest = req.url.startsWith('Employees/me');

    if (isLoggedIn && !this.auth.authToken && !isProfileRequest) {
      // Wait for authentication to settle, then send with whatever token we ended up
      // with. Waiting for a truthy token would hang forever when silent acquisition
      // fails, which is the normal state for an account that just signed up.
      return from(this.auth.profileReady()).pipe(
        switchMap(() => handleRequest(this.auth.authToken)),
      );
    }

    // Otherwise proceed immediately (anonymous or token ready)
    return handleRequest(this.auth.authToken);
  }
}
